import { RiMenuLine } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { $api } from "@/api/query";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { WikiTree } from "@/components/wiki/wiki-tree";

export const Route = createFileRoute("/_app/wiki")({
	component: WikiLayout,
});

// --- Types ---

export type WikiPageNode = {
	id: string;
	title: string;
	slug: string;
	status: string;
	position: number;
	icon?: string | null;
	parentId?: string | null;
	createdAt: string;
};

export type WikiTreeContextValue = {
	pages: WikiPageNode[];
	expandedIds: Set<string>;
	toggleExpand: (id: string) => void;
	refetchTree: () => void;
	isLoading: boolean;
};

const WikiTreeContext = createContext<WikiTreeContextValue | null>(null);

export function useWikiTree() {
	const ctx = useContext(WikiTreeContext);
	if (!ctx) throw new Error("useWikiTree must be used within WikiLayout");
	return ctx;
}

// --- localStorage persistence ---

const STORAGE_KEY = "wiki_expanded";

function loadExpanded(): Set<string> {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) return new Set(JSON.parse(stored));
	} catch {
		/* ignore */
	}
	return new Set();
}

function saveExpanded(ids: Set<string>) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
	} catch {
		/* ignore */
	}
}

// --- Layout Component ---

function WikiLayout() {
	const queryClient = useQueryClient();
	const [expandedIds, setExpandedIds] = useState<Set<string>>(loadExpanded);
	const [mobileOpen, setMobileOpen] = useState(false);

	const { data, isLoading } = $api.useQuery("get", "/api/v1/wiki");
	const pages = (data ?? []) as WikiPageNode[];

	useEffect(() => {
		saveExpanded(expandedIds);
	}, [expandedIds]);

	const toggleExpand = useCallback((id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const refetchTree = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/wiki"] });
	}, [queryClient]);

	const ctx: WikiTreeContextValue = {
		pages,
		expandedIds,
		toggleExpand,
		refetchTree,
		isLoading,
	};

	return (
		<WikiTreeContext value={ctx}>
			<div className="flex h-[calc(100vh-3.5rem-1.5rem)] -mx-6 -mb-6">
				{/* Desktop tree sidebar */}
				<aside className="hidden md:flex w-[280px] shrink-0 flex-col border-r bg-muted/30">
					{isLoading ? (
						<div className="p-3 space-y-2">
							{Array.from({ length: 6 }).map((_, i) => (
								<Skeleton key={i} className="h-7 w-full" />
							))}
						</div>
					) : (
						<WikiTree pages={pages} onNavigate={() => {}} />
					)}
				</aside>

				{/* Mobile tree toggle */}
				<div className="md:hidden fixed bottom-4 left-4 z-40">
					<Button
						size="icon"
						variant="outline"
						onClick={() => setMobileOpen(true)}
						className="shadow-lg"
					>
						<RiMenuLine className="size-4" />
					</Button>
				</div>

				{/* Mobile sheet */}
				<Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
					<SheetContent side="left" className="w-[280px] p-0">
						<SheetTitle className="sr-only">Wiki Navigation</SheetTitle>
						{isLoading ? (
							<div className="p-3 space-y-2">
								{Array.from({ length: 6 }).map((_, i) => (
									<Skeleton key={i} className="h-7 w-full" />
								))}
							</div>
						) : (
							<WikiTree pages={pages} onNavigate={() => setMobileOpen(false)} />
						)}
					</SheetContent>
				</Sheet>

				{/* Content area */}
				<main className="flex-1 overflow-y-auto p-6">
					<Outlet />
				</main>
			</div>
		</WikiTreeContext>
	);
}
